import React, { useEffect, useState } from 'react';
import opsService from '../../../services/opsService';

export default function InvoiceReview({ invoiceId, onDone }) {
  const [inv, setInv] = useState(null);
  const [header, setHeader] = useState({});
  const [items, setItems] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [newSupplierName, setNewSupplierName] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    opsService.getInvoice(invoiceId).then(async (data) => {
      setInv(data);
      setHeader({
        invoice_number: data.invoice_number || '', invoice_date: data.invoice_date || '',
        due_date: data.due_date || '', subtotal: data.subtotal || '', tax: data.tax || '',
        shipping: data.shipping || '', total: data.total || '', currency: data.currency || 'CAD',
      });
      setItems(data.line_items || []);
      const payload = JSON.parse(data.parsed_payload || '{}');
      setNewSupplierName(payload.supplier_name || '');
      const matches = await opsService.matchSuppliers(payload.supplier_name || '');
      setCandidates(matches);
      if (matches[0]) setSupplier(matches[0]);
    }).catch((e) => setError(e?.message || 'Failed to load'));
  }, [invoiceId]);

  const setItem = (i, k, v) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addItem = () => setItems([...items, { supplier_part_number: '', description: '', quantity: 1, unit_cost: 0, line_total: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const createSupplier = async () => {
    setError(null);
    const name = newSupplierName.trim();
    if (!name) { setError('Enter a supplier name to create'); return; }
    try {
      const created = await opsService.createSupplier({ name });
      setSupplier(created);
      setCandidates([created, ...candidates.filter((c) => c.id !== created.id)]);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Create supplier failed');
    }
  };

  const approve = async () => {
    setError(null);
    if (!supplier) { setError('Resolve a supplier first'); return; }
    try {
      await opsService.approveInvoice(invoiceId, { supplier_id: supplier.id, header, line_items: items });
      onDone && onDone();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Approve failed');
    }
  };

  if (!inv) return <div className="text-ink-muted">Loading…</div>;

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-ink-soft">Review invoice #{inv.id}</h3>
        <span className="text-ink-muted text-small">confidence: {inv.parse_confidence ?? '—'}</span>
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-ink-muted">Supplier:</label>
          <select className="border rounded px-2 py-1 border-border-strong" value={supplier?.id || ''}
                  onChange={(e) => setSupplier(candidates.find((c) => String(c.id) === e.target.value) || null)}>
            <option value="">— select existing —</option>
            {candidates.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <span className="text-ink-muted">or</span>
          <input className="border rounded px-2 py-1 border-border-strong" placeholder="new supplier name"
                 value={newSupplierName} onChange={(e) => setNewSupplierName(e.target.value)} />
          <button type="button" disabled={!newSupplierName.trim()} onClick={createSupplier}
                  className="px-3 py-1 bg-accent text-white rounded-lg hover:bg-accent-hover disabled:opacity-50">
            Create supplier
          </button>
        </div>
        <div className="text-small">
          {supplier
            ? <span className="text-success">✓ Supplier: {supplier.name}</span>
            : <span className="text-ink-muted">No supplier selected — pick an existing one or create a new one.</span>}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['invoice_number', 'invoice_date', 'due_date', 'currency', 'subtotal', 'tax', 'shipping', 'total'].map((f) => (
          <label key={f} className="text-small text-ink-muted">
            {f}
            <input className="mt-1 w-full border rounded px-2 py-1 border-border-strong" value={header[f] ?? ''}
                   onChange={(e) => setHeader({ ...header, [f]: e.target.value })} />
          </label>
        ))}
      </div>

      <table className="w-full text-small">
        <thead className="text-ink-muted"><tr>
          <th className="text-left">Part #</th><th className="text-left">Description</th>
          <th>Qty</th><th>Unit</th><th>Total</th><th></th>
        </tr></thead>
        <tbody>
          {items.map((it, i) => (
            <tr key={i} className="border-t">
              {['supplier_part_number', 'description', 'quantity', 'unit_cost', 'line_total'].map((k) => (
                <td key={k} className="px-1 py-1">
                  <input className="w-full border rounded px-1 py-1 border-border-strong" value={it[k] ?? ''}
                         onChange={(e) => setItem(i, k, e.target.value)} />
                </td>
              ))}
              <td><button className="text-danger" onClick={() => removeItem(i)}>✕</button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button className="text-accent hover:underline" onClick={addItem}>＋ add line</button>

      {error && <p className="text-danger">{error}</p>}
      <div className="flex justify-end gap-2">
        <a href={opsService.invoiceFileUrl(inv.id)} target="_blank" rel="noreferrer"
           className="px-4 py-2 border rounded-lg">View original PDF</a>
        <button className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-hover"
                onClick={approve}>Approve</button>
      </div>
    </div>
  );
}
